import { StyleSheet } from 'react-native';

import theme from '@themes/theme';

// Layout constants shared by the two web media viewers so their chrome
// cannot silently drift apart:
//  - MediaViewer.web.tsx        (logged-in chat / project files viewer)
//  - ImagesPreviewNotLogin.web.tsx (public `Copy link to share` viewer, TA-10293)
export const ZOOM_STEP = 25;
export const ZOOM_MIN = 25;
export const ZOOM_MAX = 200;
export const HEADER_H = 56;
export const BOTTOM_H = 148;
export const THUMB_W = 160;
export const THUMB_H = 112;
// Nav arrow glyph + its circular button (design spec, TA-10478): a plain
// arrow glyph inside a single 40px circle — dark translucent fill plus a
// white ring drawn as the button's own border, not baked into the icon SVG.
export const NAV_ICON = 24;
export const NAV_BTN_SIZE = 40;
export const NAV_BTN_BORDER_WIDTH = 2;
export const TOOLBAR_ICON = 18;
// Scroll distance from the strip's end (≈ one thumbnail) that triggers
// loading the next page (TA-9771).
export const THUMB_STRIP_END_THRESHOLD = THUMB_W;

// Shared visual chrome — header, stage, nav arrows, thumbnail strip — lifted
// out of MediaViewer.web.tsx (TA-10293) so both viewers render the same
// dimensions/colors instead of maintaining two copies. Deliberately a plain
// object (not StyleSheet.create'd) so each consumer can spread it into its
// own StyleSheet.create({ ...chromeStyles, ...ownStyles }) call alongside
// styles the other viewer doesn't need.
export const chromeStyles = {
  root: {
    flex: 1,
    backgroundColor: theme.colors.viewerChrome,
  },
  header: {
    height: HEADER_H,
    backgroundColor: theme.colors.viewerChrome,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.xxs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.scrimLight08,
    zIndex: 20,
    overflow: 'visible' as any,
    // A rapid double-click anywhere in the toolbar must not leave a text
    // highlight (TA-10615). ToolbarTooltip already covers its own anchors,
    // but a click landing in the gaps *between* controls hits this row and
    // would still select the nearest text — the `{zoom}%` label between the
    // zoom buttons, or the filename beside the rename button. The rename
    // TextInput opts back in via `userSelect: 'text'`, since renaming needs
    // a real caret and selection.
    userSelect: 'none' as any,
  },
  hLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
  },
  hCenter: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: theme.spacing.xs,
    overflow: 'visible' as any,
  },
  hRight: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    overflow: 'visible' as any,
    zIndex: 20,
  },
  senderBlock: {
    marginLeft: theme.spacing.xs,
    flexShrink: 1,
    justifyContent: 'center',
  },
  senderName: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 16,
  },
  dateLabel: {
    color: theme.colors.scrimLight50,
    fontSize: 11,
    lineHeight: 14,
    marginTop: 2,
  },
  filenameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  // Underlined to signal "click to rename" in MediaViewer, where the owner
  // can tap it. ImagesPreviewNotLogin (guest, no rename) uses its own
  // `filenameStatic` style that copies this and drops the underline instead
  // of reusing this key, since a dashed underline would be a lie for guests.
  filename: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '500',
    textDecorationLine: 'underline',
    textDecorationColor: theme.colors.scrimLight25,
    textDecorationStyle: 'dashed',
    flexShrink: 1,
  },

  // Icon buttons
  iconBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBtnHover: {
    backgroundColor: theme.colors.scrimLight10,
    borderRadius: 6,
  },
  iconGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xxs,
    overflow: 'visible' as any,
  },
  groupDivider: {
    width: 1,
    height: 20,
    backgroundColor: theme.colors.scrimLight08,
    marginHorizontal: theme.spacing.l,
  },
  zoomPct: {
    color: theme.colors.white,
    fontSize: 12,
    fontWeight: '500',
    minWidth: 38,
    textAlign: 'center',
  },

  // Image area
  imageArea: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    zIndex: 1,
    backgroundColor: theme.colors.viewerImageBg,
  },
  // Dark translucent fill + white ring drawn as the button's own border, so
  // the nav arrow stays visible regardless of the underlying image's
  // color/brightness — the plain arrow glyph has no backing of its own
  // (TA-10478).
  navBtn: {
    position: 'absolute',
    top: '50%' as any,
    marginTop: -(NAV_BTN_SIZE / 2),
    width: NAV_BTN_SIZE,
    height: NAV_BTN_SIZE,
    borderRadius: NAV_BTN_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.scrimDark50,
    borderWidth: NAV_BTN_BORDER_WIDTH,
    borderColor: theme.colors.white,
  },
  navLeft: { left: 20 },
  navRight: { right: 20 },

  // Bottom thumbnail strip
  bottomBar: {
    backgroundColor: theme.colors.viewerChrome,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.scrimLight08,
  },
  thumbStrip: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    gap: theme.spacing.xs,
  },
  thumbBorder: {
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  thumbActive: {
    borderColor: theme.colors.white,
  },
  thumbWrap: {
    borderRadius: 10,
    overflow: 'hidden',
  },
  thumb: {
    width: THUMB_W,
    height: THUMB_H,
    backgroundColor: theme.colors.scrimLight06,
  },
  thumbFileBg: {
    backgroundColor: theme.colors.scrimLight10,
  },
  thumbHoverOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.scrimDark25,
  },
} as const;
